// 🔧 voting_types.js
// ✅ Purpose: Central voting type configuration for Let'sGoVote
// ✅ Features:
// - Defines all supported voting types in one place
// - Keeps labels and descriptions consistent across admin and public pages
// - Allows existing categories to safely default to standard voting
// - Helps future admin pages render voting-type-specific setup fields
// - Helps future voting pages render the correct voting UI
// - Keeps voting type logic out of individual page files

export const VOTING_TYPES = {
  standard: {
    id: "standard",
    label: "Standard Vote",
    shortLabel: "Standard",
    description: "One voter selects one entry in a category.",
    requiresSingleEntry: true,
    supportsRanking: false,
    supportsScoreFields: false,
    supportsPoints: false,
    isDefault: true,
  },

  ranked_choice: {
    id: "ranked_choice",
    label: "Ranked Choice Voting",
    shortLabel: "Ranked Choice",
    description: "Voters rank multiple entries in order of preference.",
    requiresSingleEntry: false,
    supportsRanking: true,
    supportsScoreFields: false,
    supportsPoints: false,
    isDefault: false,
  },

  // ⚠️ Legacy configuration
  // Detailed Scorecard Voting is now implemented as a mode of
  // Points Voting rather than a standalone voting engine.
  // Kept temporarily for backwards compatibility.

  scorecard: {
    id: "scorecard",
    label: "Detailed Scorecard Voting (Legacy)",
    shortLabel: "Scorecard",
    description:
      "Legacy configuration. Use Judged Points Voting with Detailed Scorecard Mode instead.",
    requiresSingleEntry: true,
    supportsRanking: false,
    supportsScoreFields: true,
    supportsPoints: true,
    isDefault: false,
  },
  
  points: {
    id: "points",
    label: "Judged Points Voting",
    shortLabel: "Judged Points",
    description:
      "Judges score entries using either Simple Points Mode or Detailed Scorecard Mode.",
    requiresSingleEntry: true,
    supportsRanking: false,
    supportsScoreFields: true,
    supportsPoints: true,
    isDefault: false,
  },
};

// ✅ Default voting type used when older categories do not yet have votingType saved
export const DEFAULT_VOTING_TYPE = "standard";

// ✅ Dropdown-ready list for admin forms
export const VOTING_TYPE_OPTIONS = Object.values(VOTING_TYPES).map((type) => ({
  value: type.id,
  label: type.label,
  description: type.description,
}));

// ✅ Safely returns a voting type object, even if Firestore data is missing or invalid
export const getVotingTypeConfig = (votingType) => {
  return VOTING_TYPES[votingType] || VOTING_TYPES[DEFAULT_VOTING_TYPE];
};

// ✅ Safely returns the voting type id, defaulting older categories to standard
export const getSafeVotingType = (votingType) => {
  return VOTING_TYPES[votingType] ? votingType : DEFAULT_VOTING_TYPE;
};